import type { QuestDto, QuestTaskDto } from '@/api/hooks'

export const FACTION_LABELS: Record<QuestDto['faction'], string> = {
  neutral: 'Sans pavillon', guilde: 'Guilde', marine: 'Marine', corsaire: 'Corsaire', pirate: 'Pirate',
}
// Tailwind tint per faction (border/text), used by the card chip.
export const FACTION_TINT: Record<QuestDto['faction'], string> = {
  neutral: 'border-ocean-400/40 text-ocean-100',
  guilde: 'border-amber-400/40 text-amber-200',
  marine: 'border-sky-400/40 text-sky-200',
  corsaire: 'border-emerald-400/40 text-emerald-200',
  pirate: 'border-rum-400/40 text-rum-200',
}
const VERB_LABELS: Record<QuestTaskDto['verb'], string> = {
  trade_buy: 'Acheter', trade_sell: 'Vendre', raid: 'Piller', smuggle: 'Convoyer en contrebande',
  explore: 'Explorer ou reconnaître', defend: 'Repousser un raid', mission: 'Mener des missions',
  player_raid: 'Piller un capitaine', repel_player: 'Repousser un capitaine', story: 'Achever un récit',
}
const DEST_LABELS: Record<string, string> = {
  marine_port: 'un port de la Marine', merchant_outpost: 'un comptoir marchand',
  pirate_lair: 'un repaire pirate', mysterious: 'une île mystérieuse', production: 'une île de production',
  recif: 'un récif',
}

/** Human one-liner for a task objective (verb + qualifier), WITHOUT the count. */
export function taskLabel(t: QuestTaskDto): string {
  const q = t.qualifier ?? {}
  // TODO: `module` / `minSellMult` / `minGold` qualifier keys and non-'win'
  // `outcome` values are intentionally NOT surfaced here yet — no shipped
  // template uses them. The `resource` key is surfaced as an ICON next to the
  // counter (see taskResource), never as a word, so it stays out of this label.
  // `islandId`/`islandName` (island-bound steps) ARE surfaced below — a hidden
  // island constraint is exactly what left "Vendre avec succès" unsatisfiable.
  const parts: string[] = [VERB_LABELS[t.verb] ?? t.verb]
  if (typeof q.destinationType === 'string' && DEST_LABELS[q.destinationType]) parts.push(`à ${DEST_LABELS[q.destinationType]}`)
  // An island-bound task (chasse au trésor) must be done AT a specific island, or
  // it never counts. Surface the island NAME so the step isn't a bare "Vendre avec
  // succès" the player can't satisfy elsewhere. islandName is the display companion
  // stamped next to islandId at generation; fall back to a generic locator for any
  // pre-existing quest whose qualifier predates the name (still better than silence).
  if (typeof q.islandName === 'string' && q.islandName !== '') parts.push(`à ${q.islandName}`)
  else if (q.islandId) parts.push("sur l'île ciblée")
  if (q.minShips) parts.push(`(flotte ≥ ${q.minShips})`)
  if (q.minDanger) parts.push(`(danger ≥ ${q.minDanger})`)
  if (q.minRank) parts.push(`(rang ≥ ${q.minRank})`)
  if (q.outcome === 'win') parts.push('avec succès')
  return parts.join(' ')
}

/** The resource a task tracks (for an icon next to the counter), or null. Normalizes rhum→rum for the icon set. */
export function taskResource(t: QuestTaskDto): 'gold' | 'wood' | 'rum' | null {
  const r = t.qualifier?.resource
  if (r === 'gold') return 'gold'
  if (r === 'wood') return 'wood'
  if (r === 'rhum' || r === 'rum') return 'rum'
  return null
}

/** Progress fraction 0..1 for the bar. */
export function taskFraction(t: QuestTaskDto): number {
  if (t.done) return 1
  if (t.target <= 0) return 0
  return Math.max(0, Math.min(1, t.progress / t.target))
}

/** "x / target" for count/accumulate/streak; threshold shows done/à remplir. */
export function taskCounter(t: QuestTaskDto): string {
  if (t.kind === 'threshold') return t.done ? 'rempli' : 'à remplir'
  return `${t.progress} / ${t.target}`
}

/**
 * Reputation reward strength for a quest = the strongest of the two axis deltas
 * (0 if none). The DIRECTION is fully implied by the quest's faction, so we show
 * this magnitude on the faction tag itself (e.g. `Marine +4`) rather than a
 * separate pill that would just repeat the faction.
 */
export function repMagnitude(quest: QuestDto): number {
  const { loyaltyDelta, methodDelta } = quest.reward
  return Math.max(Math.abs(loyaltyDelta), Math.abs(methodDelta))
}

/** Compact FR deadline ("expire dans 1 j 23 h", "dans 5 h", "bientôt", or null if none). now() injectable for tests. */
export function deadlineLabel(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - now
  if (Number.isNaN(ms)) return null
  if (ms <= 0) return 'expirée'
  const h = Math.floor(ms / 3_600_000)
  if (h >= 24) {
    const days = Math.floor(h / 24)
    const remH = h % 24
    return remH > 0 ? `expire dans ${days} j ${remH} h` : `expire dans ${days} j`
  }
  if (h >= 1) return `expire dans ${h} h`
  return 'expire bientôt'
}

/**
 * Compact "dans X" countdown for a FUTURE ISO timestamp, without the "expire"
 * framing — used for next-offer / tournée-cooldown cues. Returns null when the
 * instant is past or absent (i.e. "available now"). now() injectable for tests.
 */
export function countdownLabel(iso: string | null, now: number = Date.now()): string | null {
  const label = deadlineLabel(iso, now)
  if (label === null || label === 'expirée') return null
  return label.replace(/^expire dans /, 'dans ').replace(/^expire bientôt$/, 'bientôt')
}
